import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
  Target,
  Heart,
  Rocket,
  MapPin,
  ArrowRight,
  Check,
  Menu,
  X,
} from "lucide-react";
import { Button, Badge } from "@/components/kit";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay: d },
});

const VALUES = [
  {
    icon: Target,
    title: "Fokus Indonesia",
    desc: "Dibangun khusus untuk pasar lokal — data bisnis Indonesia, normalisasi nomor 08xx, integrasi WhatsApp yang paling dipakai di sini.",
  },
  {
    icon: Heart,
    title: "Harga Adil",
    desc: "Kami percaya tools prospek berkualitas harus terjangkau untuk freelancer & UMKM — tanpa kontrak, transparan, dan bisa mulai gratis.",
  },
  {
    icon: Rocket,
    title: "Hemat Waktu",
    desc: "Apa yang biasanya butuh 3–5 jam riset manual, selesai dalam hitungan menit. Lebih banyak waktu untuk closing.",
  },
];

export default function About() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="bg-white text-slate-900">
      <Seo
        title="Tentang RESADA — Platform Lead Generation & Sales Automation Indonesia"
        description="RESADA membantu freelancer, agency & UMKM Indonesia menemukan klien lebih cepat lewat scraping data bisnis, manajemen lead, dan blast WhatsApp dalam satu platform terjangkau."
        path="/about"
      />
      {/* Nav */}
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo to="/" iconSize={32} />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-sky-600">
              Beranda
            </Link>
            <Link to="/#fitur" className="hover:text-sky-600">
              Fitur
            </Link>
            <Link to="/#harga" className="hover:text-sky-600">
              Harga
            </Link>
            <Link to="/#cara" className="hover:text-sky-600">
              Cara Kerja
            </Link>
            <Link to="/#kontak" className="hover:text-sky-600">
              Kontak
            </Link>
            <Link to="/about" className="text-sky-600">
              Tentang
            </Link>
            <Link to="/blog" className="hover:text-sky-600">
              Blog
            </Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="desktop-login-btn">Masuk</Button>
            </Link>
            <Link to="/register">
              <Button size="sm">Daftar Gratis</Button>
            </Link>
          </div>
          <div className="flex items-center gap-1 md:hidden">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="mobile-login-btn">
                Masuk
              </Button>
            </Link>
            <button
              className="p-2 -mr-2 text-slate-700"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Buka menu"
            >
              {menuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div
            className="md:hidden border-t border-slate-100 bg-white/95 backdrop-blur"
            data-testid="mobile-menu-panel"
          >
            <nav className="px-6 py-4 flex flex-col text-sm font-medium text-slate-700">
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Beranda
              </Link>
              <Link
                to="/#fitur"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Fitur
              </Link>
              <Link
                to="/#harga"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Harga
              </Link>
              <Link
                to="/#cara"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Cara Kerja
              </Link>
              <Link
                to="/#kontak"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Kontak
              </Link>
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 text-sky-600"
              >
                Tentang
              </Link>
              <Link
                to="/blog"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 hover:text-sky-600"
              >
                Blog
              </Link>
              <div className="flex gap-2 pt-3 mt-2 border-t border-slate-100">
                <Link
                  to="/login"
                  className="flex-1"
                  onClick={() => setMenuOpen(false)}
                >
                  <Button variant="outline" size="sm" className="w-full">
                    Masuk
                  </Button>
                </Link>
                <Link
                  to="/register"
                  className="flex-1"
                  onClick={() => setMenuOpen(false)}
                >
                  <Button size="sm" className="w-full">
                    Daftar Gratis
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center relative">
          <motion.div {...fade()}>
            <Badge color="sky" className="mb-5">
              Tentang Kami
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
              Kami bantu bisnis Indonesia{" "}
              <span className="text-sky-500">menemukan klien</span> lebih cepat.
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
              RESADA lahir dari satu masalah sederhana: mencari prospek bisnis
              itu melelahkan, memakan waktu, dan sering tidak akurat. Kami
              menyatukan scraping data, manajemen lead, dan blast WhatsApp dalam
              satu platform yang simpel & terjangkau.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <motion.div
              {...fade(i * 0.08)}
              key={v.title}
              className="card-soft p-7"
            >
              <div className="w-12 h-12 rounded-2xl bg-sky-50 grid place-items-center mb-5">
                <v.icon className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-display text-xl font-bold">{v.title}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">
                {v.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-3xl mx-auto px-6 relative">
          <h2 className="font-display text-3xl font-extrabold">Visi kami</h2>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Menjadi tools prospek #1 untuk freelancer, agency, dan UMKM di
            Indonesia. Kami ingin setiap pelaku bisnis — sekecil apapun — punya
            akses ke data & channel outreach yang dulu hanya dimiliki tim sales
            besar.
          </p>
          <ul className="mt-6 space-y-3">
            {[
              "Data bisnis real-time dari Google Maps",
              "Connect WhatsApp sendiri, bukan gateway bersama",
              "Harga transparan tanpa kontrak",
              "Dukungan dalam Bahasa Indonesia",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-slate-200">
                <Check className="w-5 h-5 text-sky-400 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl font-extrabold">
          Siap menemukan klien berikutnya?
        </h2>
        <Link to="/register" className="inline-block mt-6">
          <Button size="lg">
            Mulai Gratis Sekarang <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </section>

      <SiteFooter />
    </div>
  );
}
